import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Image,
  File,
  FileText,
  Film,
  Music,
  Download,
  Trash2,
  Plus,
  X,
  ExternalLink,
} from "lucide-react";
import { AttachmentPreview } from "./AttachmentPreview";
import { cn } from "@/lib/utils";

interface Attachment {
  id?: string;
  filename: string;
  url: string;
  mime_type?: string;
  size?: number;
}

interface AttachmentGalleryProps {
  attachments: Attachment[];
  onRemove?: (index: number) => void;
  onAddClick?: () => void;
  compact?: boolean;
  readOnly?: boolean;
  maxDisplay?: number;
}

export const AttachmentGallery = ({
  attachments,
  onRemove,
  onAddClick,
  compact = false,
  readOnly = false,
  maxDisplay = 4,
}: AttachmentGalleryProps) => {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const isImage = (attachment: Attachment) => {
    return attachment.mime_type?.startsWith("image/") || 
           /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(attachment.filename);
  };

  const getFileIcon = (attachment: Attachment) => {
    const mime = attachment.mime_type || "";
    const filename = attachment.filename.toLowerCase();

    if (mime.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|svg)$/.test(filename)) {
      return <Image className="h-4 w-4" />;
    }
    if (mime.startsWith("video/") || /\.(mp4|mov|avi|webm)$/.test(filename)) {
      return <Film className="h-4 w-4" />;
    }
    if (mime.startsWith("audio/") || /\.(mp3|wav|ogg|m4a)$/.test(filename)) {
      return <Music className="h-4 w-4" />;
    }
    if (mime === "application/pdf" || filename.endsWith(".pdf")) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = (attachment: Attachment, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(attachment.url, "_blank");
  };

  if (attachments.length === 0) {
    if (readOnly) {
      return <span className="text-muted-foreground text-sm">—</span>;
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onAddClick}
        className="gap-1 text-muted-foreground"
      >
        <Plus className="h-4 w-4" />
        Add attachment
      </Button>
    );
  }

  if (compact) {
    const displayAttachments = attachments.slice(0, 3);
    const remainingCount = attachments.length - 3;

    return (
      <div className="flex flex-wrap items-center gap-1">
        {displayAttachments.map((attachment, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="gap-1 cursor-pointer hover:bg-secondary/80"
            onClick={() => setPreviewIndex(index)}
          >
            {isImage(attachment) ? (
              <img
                src={attachment.url}
                alt={attachment.filename}
                className="h-4 w-4 rounded object-cover"
              />
            ) : (
              getFileIcon(attachment)
            )}
            <span className="max-w-[80px] truncate text-xs">
              {attachment.filename}
            </span>
          </Badge>
        ))}
        {remainingCount > 0 && (
          <Badge
            variant="outline"
            className="text-xs cursor-pointer"
            onClick={() => setPreviewIndex(3)}
          >
            +{remainingCount}
          </Badge>
        )}
        {!readOnly && onAddClick && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddClick}
            className="h-6 w-6 p-0"
          >
            <Plus className="h-3 w-3" />
          </Button>
        )}
        
        {previewIndex !== null && (
          <AttachmentPreview
            attachments={attachments}
            initialIndex={previewIndex}
            onClose={() => setPreviewIndex(null)}
          />
        )}
      </div>
    );
  }

  // Full gallery view
  const displayAttachments = attachments.slice(0, maxDisplay);
  const remainingCount = attachments.length - maxDisplay;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {displayAttachments.map((attachment, index) => (
          <div
            key={index}
            className={cn(
              "group relative rounded-lg border overflow-hidden cursor-pointer transition-shadow hover:shadow-md",
              isImage(attachment) ? "aspect-square" : "p-3"
            )}
            onClick={() => setPreviewIndex(index)}
          >
            {isImage(attachment) ? (
              <>
                <img
                  src={attachment.url}
                  alt={attachment.filename}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => handleDownload(attachment, e)}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!readOnly && onRemove && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(index);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-start gap-2">
                <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                  {getFileIcon(attachment)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {attachment.filename}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(attachment.size)}
                  </p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => handleDownload(attachment, e)}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  {!readOnly && onRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(index);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {remainingCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          onClick={() => setPreviewIndex(maxDisplay)}
        >
          View {remainingCount} more attachment{remainingCount > 1 ? "s" : ""}
        </Button>
      )}

      {!readOnly && onAddClick && (
        <Button
          variant="outline"
          size="sm"
          onClick={onAddClick}
          className="w-full gap-1"
        >
          <Plus className="h-4 w-4" />
          Add attachment
        </Button>
      )}

      {previewIndex !== null && (
        <AttachmentPreview
          attachments={attachments}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </div>
  );
};
